<template>
  <div class="panel panel-default">
    <div class="panel-heading">
      <div class="row">
        <div class="col-xs-6">
          <h5>
            My Reminders
          </h5>
        </div>

        <div v-if="!loading" style="margin-top: 4px">
          <div class="col-xs-6">
            <SelectWidget
                v-model="horizon"
                class="form-control input-sm"
                :nullable="true"
                placeholder="(today)"
                :options="horizonOptions"
            />
          </div>

          <div class="col-xs-6">
            <SelectWidget
                v-model="portal"
                class="form-control input-sm"
                :nullable="true"
                placeholder="(all portals)"
                :options="portals.map(portal => ({value: portal.id, label: portal.attributes.name}))"
            />
          </div>

          <div class="col-xs-6">
            <SelectWidget
                v-model="project"
                class="form-control input-sm"
                :nullable="true"
                placeholder="(all projects)"
                :options="projects.map(project => ({value: project.id, label: project.attributes.name}))"
            />
          </div>
        </div>
      </div>
    </div>

    <loading-spinner v-if="loading" :style="loadingSpinnerStyle"></loading-spinner>

    <ul v-else class="list-group">
      <li v-for="reminder in reminders" class="list-group-item" style="padding: 10px 0">
        <div style="display: flex; flex-direction: row">
          <div class="text-muted"
               style="flex: 1; font-size: 24px; display: flex; justify-content: center; align-items: center;">
            <i class="fa fa-bell"></i>
          </div>

          <div style="flex: 7 1 0%;">
            <div>
              <router-link :to="'/' + reminder.attributes.subject_type + '/' + reminder.attributes.subject_id">
                {{ reminder.attributes.subject_name }}
              </router-link>

              <span v-if="reminder.attributes.priority === 'high'" class="label label-danger">
                High
              </span>

              <span v-if="reminder.attributes.priority === 'medium'" class="label label-warning">
                Medium
              </span>

              <span v-if="reminder.attributes.priority === 'low'" class="label label-success">
                Low
              </span>
            </div>

            <div v-if="reminder.attributes.portals.length">
              <i>
                {{ reminder.attributes.portals.map(portal => portal.name).join(' &middot; ') }}
              </i>
            </div>

            <span>
              {{ reminder.attributes.text }}
            </span>

            <br>

            <i class="text-muted" :style="{color: getDiff(reminder) < 0 ? '#d9534f' : 'inherit'}">
              {{ getDeadlineDescription(reminder) }}
            </i>
          </div>

          <div style="flex: 1; font-size: 24px; display: flex; justify-content: flex-end; align-items: center;">
            <button type="button" class="btn btn-lg" @click="edit(reminder)" style="background-color: #ffffff">
              <i class="fa fa-edit"></i>
            </button>

            <button type="button" class="btn btn-lg" @click="postpone(reminder)" style="background-color: #ffffff">
              <i class="fa fa-clock"></i>
            </button>

            <button type="button" class="btn btn-lg" @click="resolve(reminder)" style="background-color: #ffffff">
              <i class="fa fa-check-circle"></i>
            </button>
          </div>
        </div>
      </li>

      <li v-if="reminders.length === 0" class="list-group-item text-center" style="padding: 50px;">
        <i class="fa fa-glass-cheers" style="font-size: 96px;"></i>

        <br><br>

        You have no reminders to resolve!
      </li>
    </ul>

    <PostponeReminderModal :reminder="editing" @postponed="load"/>

    <UpdateReminderModal :reminder="editing" @updated="load"/>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import moment from 'moment'
import notify from '@/notify'
import store from '@/store'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import SaveHeight from '@/mixins/SaveHeight'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import TaskListIcon from '@/pages/Dashboards/MyFocus/Panels/TaskList/TaskListIcon'
import UpdateReminderModal from '@/pages/Shared/Modals/UpdateReminder'
import PostponeReminderModal from "../../../../components/Modals/Reminders/PostponeReminderModal.vue";

export default {
  components: {
    PostponeReminderModal,
    LoadingSpinner, SelectWidget, TaskListIcon, UpdateReminderModal},

  mixins: [SaveHeight],

  data() {
    return {
      reminders: null,
      portals: null,
      projects: null,

      shared: store,

      horizon: null,
      portal: null,
      project: null,

      editing: null,

      loading: true,
    }
  },

  mounted() {
    this.load()
  },

  watch: {
    horizon() {
      this.loadReminders()
    },

    portal() {
      this.loadReminders()
    },

    project() {
      this.loadReminders()
    },
  },

  computed: {
    remindTo() {
      return moment().add(this.horizon, 'days').format('YYYY-MM-DD')
    },

    horizonOptions() {
      return [
        {value: 1, label: '+1 days'},
        {value: 2, label: '+2 days'},
        {value: 3, label: '+3 days'},
        {value: 7, label: '+7 days'},
        {value: 14, label: '+14 days'},
        {value: 30, label: '+30 days'},
        {value: 'all', label: 'All'}
      ]
    }
  },

  methods: {
    load() {
      Promise.all([
        this.getReminderPromise(),
        http.get('/api/v2/portals'),
        http.get('/api/v2/projects', {'filter[status]': 'active'}),
      ]).then(responses => {
        this.reminders = responses[0].data.data
        this.portals = responses[1].data.data
        this.projects = responses[2].data.data

        this.loading = false
      })
    },

    loadReminders() {
      this.saveHeight()

      this.loading = true

      this.getReminderPromise().then(response => {
        this.reminders = response.data.data

        this.loading = false
      })
    },

    getReminderPromise() {
      return http.get('/api/v2/reminders', {
        'filter[user_id]': this.shared.user.id,
        'filter[status]': 'unresolved',
        'filter[portal_id]': this.portal,
        'filter[project_id]': this.project,
        'filter[remind_to]': this.horizon === 'all' ? null : this.remindTo
      })
    },

    getDeadlineDescription(reminder) {
      let diff = this.getDiff(reminder)

      if (diff === 0) {
        return 'Today at ' + moment(reminder.attributes.remind_at).format('HH:mm')
      }

      if (diff === 1) {
        return 'Tomorrow at ' + moment(reminder.attributes.remind_at).format('HH:mm')
      }

      if (diff === -1) {
        return 'Yesterday at ' + moment(reminder.attributes.remind_at).format('HH:mm')
      }

      if (diff > 1) {
        return 'In ' + diff + ' days'
      }

      if (diff < 1) {
        return Math.abs(diff) + ' days ago'
      }
    },

    saveHeight() {
      if (this.$el.querySelector('.list-group')) {
        this.height = this.$el.querySelector('.list-group').clientHeight
      }
    },

    getDiff(reminder) {
      return moment(reminder.attributes.remind_at).diff(moment().startOf('day'), 'days')
    },

    resolve(reminder) {
      modal.confirm('Are you sure that you want to resolve the reminder?', ()=> {
        http.post('/api/v2/reminders/' + reminder.id + '/resolve')
            .then(() => {
              notify.success('The reminder was resolved')

              this.load()
            })
      })
    },

    postpone(reminder) {
      this.editing = reminder

      $('#postpone-reminder-modal').modal('show')
    },

    edit(logLine) {
      this.editing = logLine

      $('#update-reminder-modal').modal('show')
    }
  }
}
</script>
